
import { computed, defineComponent, getCurrentInstance, inject, onMounted, reactive, ref } from '@vue/composition-api';
import { getReportComments, createMemo, deleteMemo, likeMemo } from '@/v2/discovery/apis';

import CreateComment from '@/entities/report-insights/comments/CreateComment.vue';
import ShowComment from '@/entities/report-insights/comments/ShowComment.vue';
import Pagination from '@/entities/report-insights/comments/Pagination.vue';
import { checkIsGuestAccount } from '@/util/guest-util';

const PUBLIC = 0;
const PRIVATE = 1;

const DESC = 0;
const ASC = 1;

export default defineComponent({
  name: 'ReportComments',
  components: { ShowComment, CreateComment, Pagination },
  props: {
    data: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance();
    const vm = instance?.proxy;

    const forceRender = ref<boolean>(false);
    const isModifyMode = ref<boolean>(false);

    // const useRoute = () => {
    //   return instance?.proxy.$route;
    // };

    const useStore = () => {
      return instance?.proxy.$store;
    };

    const t = vm?.$t ? vm.$t.bind(vm) : (msg: string) => msg;
    // const { params } = useRoute();
    const { getters } = useStore();

    const storeUserId = computed(() => getters.userIdentity?.id);
    const userId = JSON.parse(localStorage.getItem('localStorageUser'))?.id || storeUserId;

    const total = ref<number>(0);
    const totalPages = ref<number>(0);
    const comments = ref<any[]>([]);
    const storedComments = ref<any[]>([]);

    const getCommentsParams = reactive({
      referenceId: props.data.docId ?? '',
      userId,
      page: 0,
      size: 10,
      isPrivate: PUBLIC,
      orderby: DESC,
    });

    const createComment = reactive({
      category: 'REPORT',
      referenceId: props.data.docId ?? '',
      parentId: null,
      userId,
      title: '',
      content: null,
      isPrivate: 0,
      multipartFiles: [],
    });

    const isPrivateFilter = computed(() => getCommentsParams.isPrivate);

    const getAllComments = async (page?: number) => {
      if (page || page >= 0) {
        getCommentsParams.page = page;
      }

      if (comments.value && comments.value.length > 10) {
        getCommentsParams.page = 0;
      }

      const data = await getReportComments(getCommentsParams);

      if (data) {
        comments.value = data.content;
        storedComments.value = data.content;

        total.value = data.totalElements;
        totalPages.value = data.totalPages;
      }
    };

    onMounted(async () => {
      await getAllComments();
    });

    const removeComment = async (comment: any) => {
      if (!comment || !userId) {
        return;
      }

      const result = checkIsGuestAccount(true);

      if (result) {
        return;
      }

      await deleteMemo({
        id: comment.id,
        userId,
      }).then(async (result: any) => {
        if (result) {
          vm?.$bvToast?.toast(`${t('briefing.comment.successDelete')}`, {
            toaster: 'b-toaster-bottom-right',
            title: `${t('briefing.comment.successTitle')}`,
            variant: 'success',
            solid: true,
            autoHideDelay: 4000,
          });

          await getAllComments();
          return;
        }

        vm?.$bvToast?.toast(`${t('briefing.comment.failedDelete')}`, {
          toaster: 'b-toaster-bottom-right',
          title: `${t('briefing.comment.failedTitle')}`,
          variant: 'danger',
          solid: true,
          autoHideDelay: 4000,
        });
      });
    };

    const initComment = () => {
      createComment.content = null;
      createComment.parentId = null;
      createComment.isPrivate = 0;
      createComment.multipartFiles = [];
      forceRender.value = !forceRender.value;
    };

    const onClickLike = async (params: any) => {
      const result = checkIsGuestAccount(true);

      if (result) {
        return;
      }

      await likeMemo(params).then(async (result: any) => {
        if (result) {
          await getAllComments();
        }
      });
    };

    const onChangeComment = (content: string) => {
      createComment.content = content;
    };

    const onCancelComment = () => {
      createComment.content = null;
      createComment.parentId = null;
      createComment.isPrivate = 0;
    };

    const onChangePrivate = (value: typeof PRIVATE | typeof PUBLIC) => {
      createComment.isPrivate = value;
    };

    const onSelectFiles = (files: any | any[]) => {
      let filesArray: any[];

      if (Array.isArray(files)) {
        filesArray = files;
      } else if (typeof files === 'object' && files !== null && 'file' in files) {
        filesArray = [files['file']];
      } else {
        filesArray = [files];
      }

      if (filesArray && filesArray.length > 0) {
        filesArray.forEach(file => {
          const index = createComment.multipartFiles.findIndex(
            (f: any) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified && f.type === file.type
          );

          if (index !== -1) {
            // 동일한 파일이 있으면 제거
            createComment.multipartFiles.splice(index, 1);
          } else {
            // 동일한 파일이 없으면 추가
            createComment.multipartFiles.push(file);
          }
        });
      }
    };

    const onSubmitSubComment = async (comment: any) => {
      const result = checkIsGuestAccount(true);

      if (result) {
        return;
      }

      const { multipartFiles, ...others } = comment;

      const memo = {
        category: 'REPORT',
        referenceId: props.data.docId ?? '',
        userId,
        ...others,
      };

      await createMemo({ memo, multipartFiles }).then(async (result: any) => {
        if (result) {
          vm?.$bvToast?.toast(`${t('briefing.comment.successCreateContent')}`, {
            toaster: 'b-toaster-bottom-right',
            title: `${t('briefing.comment.successTitle')}`,
            variant: 'success',
            solid: true,
            autoHideDelay: 4000,
          });

          await getAllComments();
          return;
        }

        if (!result) {
          vm?.$bvToast?.toast(`${t('briefing.comment.failedCreateContent')}`, {
            toaster: 'b-toaster-bottom-right',
            title: `${t('briefing.comment.failedTitle')}`,
            variant: 'danger',
            solid: true,
            autoHideDelay: 4000,
          });
        }
      });
    };

    const closeReply = ref<boolean>(false);

    const onSubmitComment = async (mode: number, parentId?: number) => {
      const result = checkIsGuestAccount(true);

      if (result) {
        return;
      }

      if (!createComment.content) {
        vm?.$bvToast?.toast('Please enter content before submitting', {
          toaster: 'b-toaster-bottom-right',
          title: 'Unable to post comments',
          variant: 'info',
          solid: true,
          autoHideDelay: 4000,
        });
        return;
      }

      const { multipartFiles, ...memo } = createComment;

      if (parentId) {
        Object.assign(memo, { parentId });
      }

      await createMemo({ memo, multipartFiles }).then(async (result: any) => {
        if (result) {
          vm?.$bvToast?.toast(`${t('briefing.comment.successCreateContent')}`, {
            toaster: 'b-toaster-bottom-right',
            title: `${t('briefing.comment.successTitle')}`,
            variant: 'success',
            solid: true,
            autoHideDelay: 4000,
          });

          await getAllComments();
          return;
        }

        if (!result) {
          vm?.$bvToast?.toast(`${t('briefing.comment.failedCreateContent')}`, {
            toaster: 'b-toaster-bottom-right',
            title: `${t('briefing.comment.failedTitle')}`,
            variant: 'danger',
            solid: true,
            autoHideDelay: 4000,
          });
        }
      });

      if (parentId) {
        closeReply.value = !closeReply.value;
      }

      // init
      initComment();
    };

    const getOwnerUser = () => {
      if (props.data) {
        return props.data?.owner_user_key;
      }

      return '';
    };

    const onChangePrivateComments = async (isPrivateMode: boolean) => {
      getCommentsParams.isPrivate = isPrivateMode ? PRIVATE : PUBLIC;

      await getAllComments();
    };

    const onChangeSort = async (order: boolean) => {
      getCommentsParams.orderby = order ? DESC : ASC;

      await getAllComments();
    };

    const onModifyComment = async (result: any) => {
      if (result) {
        vm?.$bvToast?.toast(`${t('briefing.comment.successModifyContent')}`, {
          toaster: 'b-toaster-bottom-right',
          title: `${t('briefing.comment.successTitle')}`,
          variant: 'success',
          solid: true,
          autoHideDelay: 4000,
        });

        await getAllComments();
        return;
      }

      vm?.$bvToast?.toast(`${t('briefing.comment.failedModifyContent')}`, {
        toaster: 'b-toaster-bottom-right',
        title: `${t('briefing.comment.failedTitle')}`,
        variant: 'danger',
        solid: true,
        autoHideDelay: 4000,
      });
    };

    return {
      userId,
      comments,
      storedComments,
      total,
      totalPages,
      forceRender,
      isModifyMode,
      closeReply,
      isPrivateFilter,
      initComment,
      removeComment,
      onClickLike,
      onSelectFiles,
      onChangeComment,
      onCancelComment,
      onChangePrivate,
      onSubmitSubComment,
      onSubmitComment,
      getOwnerUser,
      getAllComments,
      onChangePrivateComments,
      onChangeSort,
      onModifyComment,
    };
  },
});
